/**
 * 镜权等平台使用易宝 H5 收银台（cash.yeepay.com 等）时，网关可能在返回体中带跳转链接。
 * 部分链接需携带用户交易/支付密码字段；易宝开放文档中常见为驼峰 tradePassword（以实际网关为准）。
 * @see https://open.yeepay.com/docs （标准收银台 / 聚合支付等产品说明）
 */

const YEEPAY_HOST = /yeepay\.(com|cn)/i;
const HTTP_URL = /^https?:\/\//i;

/** 从 order/pay 响应中取出 orderStr（H5 会写入 sessionStorage 并跳转 /pay/） */
export function extractOrderPayStr(res) {
    const d = res?.data;
    if (typeof d === "string" && d.trim())
        return d.trim();
    if (d && typeof d === "object" && typeof d.orderStr === "string" && d.orderStr.trim())
        return d.orderStr.trim();
    return "";
}

/** 从订单支付接口响应中取出收银台跳转地址（兼容多种字段名） */
export function extractPayCashierUrl(res) {
    const d = res?.data;
    if (!d || typeof d !== "object") {
        if (typeof d === "string" && HTTP_URL.test(d.trim()))
            return d.trim();
        return "";
    }
    const keys = [
        "payUrl",
        "cashierUrl",
        "payCashierUrl",
        "cashier",
        "url",
        "h5Url",
        "h5url",
        "redirectUrl",
        "tokenUrl",
        "requestUrl"
    ];
    for (const k of keys) {
        const v = d[k];
        if (typeof v === "string" && HTTP_URL.test(v.trim()))
            return v.trim();
    }
    const orderStr = extractOrderPayStr(res);
    if (!orderStr)
        return "";
    const stripped = orderStr.replace(/^default:/i, "").trim();
    if (HTTP_URL.test(stripped))
        return stripped;
    if (YEEPAY_HOST.test(stripped))
        return stripped.startsWith("http") ? stripped : `https://${stripped}`;
    return "";
}

/** 是否存在需外链完成的易宝支付参数（orderStr 非 URL 也算未完成） */
export function hasYeepayPendingPayload(res) {
    if (extractPayCashierUrl(res))
        return true;
    const orderStr = extractOrderPayStr(res);
    return Boolean(orderStr);
}

/**
 * 将 6 位支付密码写入易宝链接查询参数，避免模板中写死 tradePassword。
 * 会更新已有 tradePassword / trade_password，否则追加 tradePassword。
 */
export function applyTradePasswordToYeepayUrl(url, tradePassword) {
    if (!url || typeof url !== "string" || !YEEPAY_HOST.test(url))
        return url;
    const pwd = String(tradePassword ?? "").trim();
    if (!/^\d{6}$/.test(pwd))
        return url;
    try {
        const u = new URL(url);
        const keys = ["tradePassword", "trade_password"];
        let set = false;
        for (const k of keys) {
            if (u.searchParams.has(k)) {
                u.searchParams.set(k, pwd);
                set = true;
            }
        }
        if (!set) {
            u.searchParams.set("tradePassword", pwd);
        }
        return u.toString();
    }
    catch {
        let out = url;
        out = out.replace(/([?&]tradePassword=)[^&]*/gi, (_, p) => `${p}${encodeURIComponent(pwd)}`);
        out = out.replace(/([?&]trade_password=)[^&]*/gi, (_, p) => `${p}${encodeURIComponent(pwd)}`);
        if (!/[?&]tradePassword=/i.test(out) && !/[?&]trade_password=/i.test(out)) {
            const sep = out.includes("?") ? "&" : "?";
            out = `${out}${sep}tradePassword=${encodeURIComponent(pwd)}`;
        }
        return out;
    }
}

/** 镜权 H5 订单页，供无法解析的 orderStr 时人工完成支付 */
export function buildJqOrderH5Url(orderId) {
    const id = String(orderId ?? "").trim();
    if (!id)
        return "";
    return `https://h5.jingquan.art/order/?orderid=${encodeURIComponent(id)}`;
}
